import {
  ConcreteEntityRights,
  DomainEntityWithRights,
  EntityRightsTypes,
  EntityWithActions,
} from "@/types/domain";
import {
  ActionModel,
  ActionResource,
  ActionType,
  ConcreteEntityRightType,
  RightsActionsInternalLib,
} from "@/types/rights";
import { removeField } from "@lib/common";

/**
 * Extracts an array of actions from a server object containing action definitions.
 *
 * This function processes the `actions` property of the given server object, which is
 * expected to be an array of action objects. Each action object should contain a `name` property
 * in the format "resource:action", where `resource` may contain `/` (e.g. `pipeline/instance:restart`).
 * The name is split on the **last** `:` so the action segment is always a single token.
 *
 * If the `actions` property is undefined or null, the function returns an empty array.
 * If a malformed `name` string is encountered, it is filtered out from the resulting array.
 *
 * @param {EntityWithActions} [o] - The server object containing the actions to extract. Maybe undefined.
 * @returns {ActionModel[]} An array of extracted `ActionModel` objects with `resource`, `action` and `rules` properties.
 */
const extractActionsFromServerObject = (o?: EntityWithActions): ActionModel[] =>
  (o?.actions ?? [])
    .map((action) => {
      const name = action.name;
      const lastColon = name.lastIndexOf(":");
      if (lastColon <= 0 || lastColon === name.length - 1) {
        return null;
      }
      const resource = name.slice(0, lastColon);
      const actionType = name.slice(lastColon + 1);
      if (!resource || !actionType) {
        return null;
      }

      return {
        resource: resource as ActionResource,
        action: actionType as ActionType,
        rules: action.rules,
        metadata: action.metadata,
      } as ActionModel;
    })
    .filter((a) => a !== null) as ActionModel[];

/**
 * Determines the concrete rights or permissions for a given action on a specific resource using a predefined set of rules.
 *
 * @param {ActionModel[]} actions - A list of action models representing the mapping of actions, resources, and their respective rules.
 * @param {ActionResource} resource - The specific resource for which the rights are being determined.
 * @param {ActionType} action - The type of action being verified against the resource.
 * @returns {ConcreteEntityRightType} The associated rights or rules for the specified action and resource, or a boolean indicating whether the action exists without explicitly defined rules.
 */
const can = (
  actions: ActionModel[],
  resource: ActionResource,
  action: ActionType,
): ConcreteEntityRightType => {
  const foundAction = actions.find((a) => a?.action === action && a?.resource === resource);
  if (foundAction?.rules !== undefined) {
    return foundAction?.rules;
  }
  return foundAction !== undefined;
};

const firstAllowedRight = (...rights: ConcreteEntityRightType[]): ConcreteEntityRightType => {
  const allowedRight = rights.find((right) => right === true || Array.isArray(right));

  return allowedRight ?? false;
};

/**
 * A function that removes the "actions" field from the given object.
 *
 * @function
 * @param {object} [o] - The object from which to remove the "actions" field. This parameter is optional.
 * @returns {*} The result of removing the "actions" field from the input object. The return type is determined by the `removeField` function and its implementation.
 */
const removeActions = <T>(o?: T) => removeField(o, "actions");

/**
 * Defines a collection of internal methods to manage and evaluate access rights to specific resources based on provided actions.
 *
 * Properties:
 * - `can`: A utility function for determining access rights to a resource.
 * - `canList`: Verifies if the "list" action is permitted on a given resource based on the action model.
 * - `canCreate`: Verifies if the "create" action is permitted on a given resource based on the action model.
 * - `canRead`: Verifies if the "read" action is permitted on a given resource based on the action model.
 * - `canPatch`: Verifies if the "patch" action is permitted on a given resource based on the action model.
 * - `canDelete`: Verifies if the "delete" action is permitted on a given resource based on the action model.
 * - `canEntityRights`: Evaluates the read, delete, and patch access rights on a given resource and returns a record of permissions for the specified entity rights types.
 */
const rightsActionsInternal: RightsActionsInternalLib = {
  /**
   * Determines whether a given task can be performed based on specific conditions
   * or requirements.
   *
   * @param {any} input - The input or conditions that are evaluated to determine
   * if the task is permissible.
   * @returns {boolean} - Returns `true` if the task can be performed, otherwise `false`.
   */
  can,
  /**
   * Determines if the specified actions for a resource include the permission to perform the "list" action.
   *
   * @param {ActionModel[]} actions - An array of action models that define the permissions or capabilities for various actions.
   * @param {ActionResource} resource - The resource for which the "list" action is being checked.
   */
  canList: (actions: ActionModel[], resource: ActionResource) => can(actions, resource, "list"),
  /**
   * Determines if the "create" action is permitted for a given resource based on the provided actions.
   *
   * @param {ActionModel[]} actions - An array of action models representing allowed actions.
   * @param {ActionResource} resource - The resource for which the "create" action is being checked.
   */
  canCreate: (actions: ActionModel[], resource: ActionResource) => can(actions, resource, "create"),
  /**
   * Determines if the specified "read" action can be performed
   * on a given resource based on the action model provided.
   *
   * @param {ActionModel[]} actions - An array of action models that the user is authorized to perform.
   * @param {ActionResource} resource - The resource upon which the "read" action is being checked.
   */
  canRead: (actions: ActionModel[], resource: ActionResource) => can(actions, resource, "read"),
  /**
   * Determines if the "patch" action can be performed on the given resource
   * based on the provided actions and resource details.
   *
   * @function canPatch
   * @param {ActionModel[]} actions - An array of actions defining the permitted actions for resources.
   * @param {ActionResource} resource - The resource object on which the "patch" action is being checked.
   */
  canPatch: (actions: ActionModel[], resource: ActionResource) => can(actions, resource, "patch"),
  /**
   * Determines whether a specified "delete" action can be performed on a given resource.
   *
   * @param {ActionModel[]} actions - An array of action models representing the user's available actions.
   * @param {ActionResource} resource - The resource on which the "delete" action is to be evaluated.
   */
  canDelete: (actions: ActionModel[], resource: ActionResource) => can(actions, resource, "delete"),
  /**
   * Determines the rights of an entity for a given set of actions and resources.
   *
   * @param {ActionModel[]} actions - The list of actions to evaluate.
   * @param {ActionResource} resource - The resource being evaluated for access rights.
   * @returns {Record<EntityRightsTypes, ConcreteEntityRightType>} An object representing the rights of the entity,
   * including `canRead`, `canDelete`, and `canPatch`.
   */
  canEntityRights: (
    actions: ActionModel[],
    resource: ActionResource,
  ): Record<EntityRightsTypes, ConcreteEntityRightType> => ({
    canRead: rightsActionsInternal.canRead(actions, resource),
    canDelete: rightsActionsInternal.canDelete(actions, resource),
    canPatch: rightsActionsInternal.canPatch(actions, resource),
  }),
  /**
   * Determines if any of a set of actions can be performed on a given resource.
   *
   * @param {ActionModel[]} actions - The list of actions to evaluate.
   * @param {ActionResource[]} resources - The resources being evaluated for access rights.
   * @param {ActionType} action - The specific action to check for.
   */
  canAny: (
    actions: ActionModel[],
    resources: ActionResource[],
    action: ActionType,
  ): ConcreteEntityRightType =>
    firstAllowedRight(...resources.map((resource) => can(actions, resource, action))),
  /**
   * Determines the rights available for all supported entity types on a set of resources.
   *
   * @param {ActionModel[]} actions - The list of actions to evaluate.
   * @param {ActionResource[]} resources - The resources being evaluated for access rights.
   */
  canEntityRightsForAnyResource: (actions: ActionModel[], resources: ActionResource[]) => ({
    canRead: rightsActionsInternal.canAny(actions, resources, "read"),
    canPatch: rightsActionsInternal.canAny(actions, resources, "patch"),
    canDelete: rightsActionsInternal.canAny(actions, resources, "delete"),
  }),
};

/**
 * An object containing utility methods related to rights actions.
 *
 * @property {function} parseActions - A function that processes entity actions and executes a callback to determine entity rights.
 * @template T - A type that extends EntityWithActions, representing entities that possess actions.
 * @template U - A type that extends DomainEntity with T, representing domain entities mapped from T.
 * @param {T} serverObject - The entity object from the server containing actions to be processed.
 * @param {function} callback - A callback function that takes an array of action models and a utility library to produce concrete entity rights.
 * @returns {U} The modified entity object with updated rights information, excluding the raw actions.
 */
export const rightsActions = {
  /**
   * Parses actions from the provided server object and processes them through a callback to generate
   * rights for the entity. It then removes the actions from the server object and returns the resulting entity.
   *
   * @template T - Extends EntityWithActions. The type of the server object, which includes actions.
   * @template U - Extends DomainEntity<T>. The type of the domain entity returned after processing.
   * @param {T} serverObject - The server object containing actions to be parsed and processed.
   * @param {function(ActionModel[], RightsActionsInternalLib): ConcreteEntityRights<never>} callback - A function that takes the extracted actions
   * and a right library, then generates rights for the entity based on the actions.
   * @returns {U} The domain entity resulting from the parsed and processed server object, with actions removed.
   */
  parseActions: <
    T extends EntityWithActions,
    K extends string,
    U extends DomainEntityWithRights<K>,
  >(
    serverObject: T,
    callback: (
      actions: ActionModel[],
      canFnLib: RightsActionsInternalLib,
    ) => ConcreteEntityRights<K>,
  ): U =>
    // @ts-expect-error typing wrong with T and U
    removeActions({
      ...serverObject,
      rights: callback(extractActionsFromServerObject(serverObject), rightsActionsInternal),
    }),
  /**
   * Determines whether a given field can be patched based on the provided rights and field path.
   *
   * @param {ConcreteEntityRightType} right - The access right definition.
   *        It could be:
   *        - `true`: grants full access to all fields.
   *        - `false`: denies access to all fields.
   *        - An array of string rules defining specific access permissions:
   *          - Rules starting with "O " (open) indicate access granted.
   *          - Rules starting with "C " (close) indicate access denied.
   *          - Wildcard rules ("O *" or "C *") apply to all fields, unless overridden by more specific rules.
   * @param {string} fieldPath - The path of the field to evaluate against the rights.
   * @returns {boolean} - Returns `true` if the field can be patched, `false` otherwise.
   */
  canPatchField: (right: ConcreteEntityRightType, fieldPath: string): boolean => {
    if (right === true) {
      return true;
    }
    if (right === false) {
      return false;
    }
    if (Array.isArray(right)) {
      if (right.length === 0) {
        return true;
      }

      // Start with default: assume "O *" at the beginning if not explicitly present
      let isAllowed = true;

      // Process rules sequentially - order matters!
      for (const rule of right) {
        const isOpen = rule.startsWith("O ");
        const isClose = rule.startsWith("C ");

        if (!isOpen && !isClose) {
          continue;
        }

        const rulePath = rule.substring(2); // Remove "O " or "C " prefix

        // Wildcard rules apply to everything
        if (rulePath === "*") {
          isAllowed = isOpen;
          continue;
        }

        // Check if the rule matches the field path
        if (matchesPath(fieldPath, rulePath)) {
          isAllowed = isOpen;
        }
      }

      return isAllowed;
    }

    return false;
  },
};

/**
 * Checks if a field path matches a rule path pattern.
 * Supports wildcards (*) in rule paths.
 * Each wildcard (*) can match one or more path segments.
 *
 * Examples:
 * - "content.*.email" matches: "content.field.email", "content.a.b.email", "content.x.y.z.email"
 * - "content.*.*.email" matches: "content.a.b.email", "content.a.b.c.email" (at least 2 levels)
 *
 * @param {string} fieldPath - The actual field path to check (e.g., "data.field.nestedField")
 * @param {string} rulePath - The rule path pattern (e.g., "data.*.nestedField" or "field")
 * @returns {boolean} True if the field path matches the rule pattern
 */
function matchesPath(fieldPath: string, rulePath: string): boolean {
  // If the rule has no wildcards, do exact match
  if (!rulePath.includes("*")) {
    return fieldPath === rulePath;
  }

  const fieldParts = fieldPath.split(".");
  const ruleParts = rulePath.split(".");

  // Minimum required parts: non-wildcard parts + wildcard count (each * matches at least 1 level)
  const minRequiredParts = ruleParts.length;

  // Field must have at least as many parts as the minimum required
  if (fieldParts.length < minRequiredParts) {
    return false;
  }

  // Try to match the pattern using backtracking
  return matchesPathRecursive(fieldParts, 0, ruleParts, 0);
}

/**
 * Recursive helper function to match field parts against rule parts with wildcard support.
 * Each wildcard can match one or more consecutive parts.
 *
 * @param {string[]} fieldParts - Array of field path segments
 * @param {number} fieldIndex - Current index in fieldParts
 * @param {string[]} ruleParts - Array of rule path segments (may contain wildcards)
 * @param {number} ruleIndex - Current index in ruleParts
 * @returns {boolean} True if the remaining parts match
 */
function matchesPathRecursive(
  fieldParts: string[],
  fieldIndex: number,
  ruleParts: string[],
  ruleIndex: number,
): boolean {
  // If we've processed all rule parts
  if (ruleIndex >= ruleParts.length) {
    // Match only if we've also processed all field parts
    return fieldIndex >= fieldParts.length;
  }

  // If we've processed all field parts but still have rule parts
  if (fieldIndex >= fieldParts.length) {
    return false;
  }

  const currentRulePart = ruleParts[ruleIndex];

  if (currentRulePart === "*") {
    // Wildcard can match one or more parts
    // Try matching 1, 2, 3, ... parts until we find a match
    for (let matchCount = 1; matchCount <= fieldParts.length - fieldIndex; matchCount++) {
      // Try to match the rest of the pattern after consuming matchCount parts
      if (matchesPathRecursive(fieldParts, fieldIndex + matchCount, ruleParts, ruleIndex + 1)) {
        return true;
      }
    }
    return false;
  } else {
    // Non-wildcard part must match exactly
    if (fieldParts[fieldIndex] !== currentRulePart) {
      return false;
    }
    // Continue matching the next parts
    return matchesPathRecursive(fieldParts, fieldIndex + 1, ruleParts, ruleIndex + 1);
  }
}
